export interface NamingItem {
  clientId: string;
  namingFile: File;
  originalName: string;
}

export interface NamingSuggestion {
  clientId: string;
  suggestedName: string;
  error?: string;
}

type SuggestFn = (args: {
  projectId: number;
  items: Array<{ clientId: string; file: File }>;
}) => Promise<{ suggestions: NamingSuggestion[] }>;

const CHUNK_SIZE = 10;

export const batchSuggestNames = async (
  suggest: SuggestFn,
  projectId: number,
  items: NamingItem[],
  onProgress?: (done: number, total: number) => void
): Promise<NamingSuggestion[]> => {
  const results: NamingSuggestion[] = [];
  const chunks = Array.from({ length: Math.ceil(items.length / CHUNK_SIZE) }, (_, index) =>
    items.slice(index * CHUNK_SIZE, (index + 1) * CHUNK_SIZE)
  );

  await chunks.reduce<Promise<void>>(async (chain, chunk, index) => {
    await chain;
    try {
      const response = await suggest({
        projectId,
        items: chunk.map(({ clientId, namingFile, originalName }) => {
          const ext = originalName.split('.').pop()?.toLowerCase() ?? 'jpg';
          const namedFile = new File([namingFile], `${clientId}.${ext}`, { type: namingFile.type });
          return { clientId, file: namedFile };
        }),
      });
      results.push(...response.suggestions);
    } catch {
      chunk.forEach(({ clientId, originalName }) => {
        results.push({ clientId, suggestedName: originalName, error: 'LLM_FAILED' });
      });
    }

    onProgress?.(Math.min((index + 1) * CHUNK_SIZE, items.length), items.length);
  }, Promise.resolve());

  return results;
};
